<script lang="ts">
  import type { LeaderboardViewState } from "../../leaderboard/leaderboardAdapter";
  import type { ScoreSnapshot } from "../../scoring/ScoreSystem";
  import type { Translate } from "../i18n";
  import type { BestRun } from "../preferences";
  import LeaderboardBoard from "./LeaderboardBoard.svelte";

  const {
    score,
    leaderboard,
    best,
    isNewBest = false,
    t,
    onRestart,
    nameError = "",
    onSaveName,
  }: {
    score: ScoreSnapshot;
    leaderboard: LeaderboardViewState;
    best?: BestRun | null;
    isNewBest?: boolean;
    t: Translate;
    onRestart: () => void;
    /** Why the last name was refused, if it was. */
    nameError?: string;
    onSaveName?: (name: string) => void;
  } = $props();

  let name = $state("");

  /** Only a saved run has a row to rename. */
  const canName = $derived(
    Boolean(onSaveName) &&
      leaderboard.status === "saved" &&
      leaderboard.playerName === null,
  );

  function saveName(event: SubmitEvent): void {
    event.preventDefault();
    const trimmed = name.trim();
    if (trimmed) {
      onSaveName?.(trimmed);
    }
  }

  function clock(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const rest = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${rest}`;
  }

  const status = $derived(
    leaderboard.status === "submitting"
      ? t("gameover.saving")
      : leaderboard.status === "saved"
        ? leaderboard.rank
          ? t("gameover.savedRank", { rank: leaderboard.rank })
          : t("gameover.saved")
        : leaderboard.status === "unavailable"
          ? t("gameover.unavailable")
          : "",
  );
</script>

<section class="overlay" aria-label={t("gameover.title")}>
  <div class="dialog">
    <h2>{t("gameover.title")}</h2>
    <p>{t("gameover.subtitle")}</p>
    <div class="dialog__stats">
      <span class="dialog__stat">
        <b>{score.landings}</b><span>{t("gameover.landings")}</span>
      </span>
      <span class="dialog__stat">
        <b>{score.departures}</b><span>{t("gameover.departures")}</span>
      </span>
      <span class="dialog__stat">
        <b>{clock(score.durationSeconds)}</b><span
          >{t("gameover.duration")}</span
        >
      </span>
    </div>
    {#if isNewBest}
      <p class="dialog__best">{t("gameover.newBest")}</p>
    {:else if best}
      <p class="dialog__best">
        {t("gameover.best", { landings: best.landings })}
      </p>
    {/if}
    {#if status}
      <p aria-live="polite">{status}</p>
    {/if}

    {#if canName}
      <form class="dialog__name" onsubmit={saveName}>
        <input
          type="text"
          maxlength="24"
          bind:value={name}
          placeholder={t("gameover.namePlaceholder")}
          aria-label={t("gameover.namePlaceholder")}
        />
        <button type="submit">{t("gameover.nameSave")}</button>
      </form>
      {#if nameError}
        <p class="dialog__error" aria-live="polite">{nameError}</p>
      {/if}
    {:else if leaderboard.playerName}
      <p aria-live="polite">
        {t("gameover.nameSaved", { name: leaderboard.playerName })}
      </p>
    {/if}

    {#if leaderboard.boardStatus !== "idle"}
      <LeaderboardBoard
        entries={leaderboard.entries}
        status={leaderboard.boardStatus}
        {t}
      />
    {/if}

    <button type="button" onclick={onRestart}>{t("gameover.again")}</button>
  </div>
</section>

<style>
  .dialog__best {
    font-weight: 700;
  }

  .dialog__name {
    display: flex;
    gap: 0.4rem;
    width: 100%;
    max-width: 20rem;
  }

  .dialog__name input {
    flex: 1;
    min-width: 0;
    font: inherit;
    padding: 0.45rem 0.6rem;
    border: 1px solid var(--panel-border);
    border-radius: 0.16rem;
    background: rgb(242 246 249 / 0.08);
    color: var(--panel-text);
  }

  .dialog__name input::placeholder {
    color: var(--panel-text);
    opacity: 0.45;
  }
</style>
